/**
 * NewInductionModal
 *
 * Modal form for creating a new induction batch (pulling logs from cold storage).
 */

import { useState } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2 } from 'lucide-react'

export default function NewInductionModal({ open, onOpenChange, onSubmit, purchases }) {
  const availablePurchases = purchases.filter(p => p.logs_remaining > 0)

  const [formData, setFormData] = useState({
    purchase_id: '',
    induction_date: toLocalDateString(new Date()),
    log_count: '',
    notes: ''
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const selectedPurchase = availablePurchases.find(p => p.id === formData.purchase_id)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const logCount = parseInt(formData.log_count, 10)
      if (selectedPurchase && logCount > selectedPurchase.logs_remaining) {
        throw new Error(`Only ${selectedPurchase.logs_remaining} logs available in this purchase`)
      }

      await onSubmit({
        purchase_id: formData.purchase_id,
        strain_id: selectedPurchase.strain_id,
        induction_date: formData.induction_date,
        log_count: logCount,
        log_weight_kg: selectedPurchase.unit_weight_kg,
        notes: formData.notes || null
      })
      onOpenChange(false)
      setFormData({
        purchase_id: '',
        induction_date: toLocalDateString(new Date()),
        log_count: '',
        notes: ''
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short'
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Induction</DialogTitle>
        </DialogHeader>

        {availablePurchases.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground">
            No purchases with available logs. Record a purchase first.
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Source Purchase</Label>
              <Select
                value={formData.purchase_id}
                onValueChange={(val) => handleChange('purchase_id', val)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a purchase..." />
                </SelectTrigger>
                <SelectContent>
                  {availablePurchases.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.purchase_code || formatDate(p.purchase_date)} ({p.logs_remaining} available)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="induction_date">Induction Date</Label>
                <Input
                  id="induction_date"
                  type="date"
                  value={formData.induction_date}
                  onChange={(e) => handleChange('induction_date', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="log_count">
                  Number of Logs
                  {selectedPurchase && (
                    <span className="text-muted-foreground font-normal"> (max {selectedPurchase.logs_remaining})</span>
                  )}
                </Label>
                <Input
                  id="log_count"
                  type="number"
                  min="1"
                  max={selectedPurchase?.logs_remaining || 999}
                  value={formData.log_count}
                  onChange={(e) => handleChange('log_count', e.target.value)}
                  placeholder={selectedPurchase ? String(selectedPurchase.logs_remaining) : '50'}
                  required
                />
              </div>
            </div>

            {selectedPurchase && formData.log_count && (
              <div className="rounded-lg bg-muted/50 p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total substrate weight</span>
                  <span className="font-medium">
                    {(parseInt(formData.log_count) * selectedPurchase.unit_weight_kg).toFixed(1)} kg
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Log cost</span>
                  <span className="font-medium">
                    £{(parseInt(formData.log_count) * Number(selectedPurchase.cost_per_log)).toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Optional notes..."
              />
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !formData.purchase_id}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Induction
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
